"use client";
import React, { useState, useRef } from "react";
import { useDialog } from "@/hooks/useDialog";
import { useAuth } from "@/contexts/AuthContext";
import Breadcrumb from "@/components/Breadcrumbs/Breadcrumb";
import Stepper2 from "@/components/Stepper/Stepper2";
import Step1 from "./components/Step1";
import Step2 from "./components/Step2";
import Step3 from "./components/Step3";
import Step4 from "./components/Step4";
import Step5 from "./components/Step5";
import Step6 from "./components/Step6";
import Step7 from "./components/Step7";
import Step8 from "./components/Step8";
import Step9 from "./components/Step9";
import Step10 from "./components/Step10";
import alcabalasApi from "@/api/Alcabalas";
import solvenciaApi from "@/api/Solvencia";
import HistoriaDominioApi from "@/api/HistoriaDominio";
import gustavoToJSON from "@/utils/gustavoToJSON";
import convertToBase64 from "@/utils/convertToBase64File";
import { formatCadenaPredio } from "@/utils/format";
const LiquidacionAlcabales = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPredio, setSelectedPredio] = useState(null);
  const [solvencia, setSolvencia] = useState(null);
  const [impuestoCalculado, setImpuestoCalculado] = useState(null);
  const [requisitos, setRequisitos] = useState([]);
  const [certificadosDisponibles, setCertificadosDisponibles] = useState([]);
  const [selectedCertificado, setSelectedCertificado] = useState(null);
  const [certificadoEnTramite, setCertificadoEnTramite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [datosComprador, setDatosComprador] = useState(null);
  const [dataTramite, setDataTramite] = useState({});
  const [step9Files, setStep9Files] = useState({});
  const { user } = useAuth();
  const { show } = useDialog();
  const mapRef = useRef();
  const step6Ref = useRef();
  const step9Ref = useRef();

  const steps = [
    {
      title: "Información",
      body: (
        <Step1 />
      ),
      showNext: true
    },
    {
      title: "Seleccione el predio",
      body: user ? (
        <Step2
          user={user}
          mapRef={mapRef}
          selectedPredio={selectedPredio}
          setSelectedPredio={setSelectedPredio}
        />
      ) : (
        <div>Cargando información del usuario...</div>
      ),
      showNext: true,
      onNext: async () => {
        if (!selectedPredio) {
          show({
            type: "error",
            title: "Sin datos",
            message: "No se han seleccionado ningun predio",
          });
          return false;
        }
        return true;
      }
    },
    {
      title: "Solvencia de registro de la propiedad",
      body: (
        <Step3
          solvencia={solvencia}
          setSolvencia={setSolvencia}
          isLoading={isLoading}
        />
      ),
      onNext: async () => {
        if (!solvencia) {
          show({
            type: "error",
            title: "Sin datos",
            message:
              "No se ha ingresado el número de solvencia del Registro de la Propiedad",
          });
          return false;
        } else {
          setIsLoading(true);
          try {
            const clavePredial = formatCadenaPredio(selectedPredio.clave);
            const res = await alcabalasApi.getCertificadoAvaluo(clavePredial, solvencia);
            setRequisitos(res.data.data.requisitos);
            const filteredCertificados = res.data.data.certificadoPendientes.filter(
              (certificado) => formatCadenaPredio(certificado.clave) === clavePredial
            );
            setCertificadosDisponibles(filteredCertificados);
          } catch (error) {
            console.error("Error fetching certificado avaluo:", error);
            show({
              type: "error",
              title: "Error",
              message: "Ocurrió un error al obtener el certificado de avalúo.",
            });
            return false;
          } finally {
            setIsLoading(false);
          }
        }
        return true;
      },
      showNext: () => !isLoading
    },
    {
      title: "Requisitos",
      body: <Step4 requisitos={requisitos} />,
      showNext: true,
      onNext: async () => {
        const hasDeniedRequisite = requisitos.some(req => !req.Valor);
        if (hasDeniedRequisite) {
          show({
            type: "error",
            title: "Requisitos negados",
            message:
              "No se puede continuar el trámite ya que tiene requisitos negados",
          });
          return false;
        }
        return true;
      },
    },
    {
      title: "Certificados",
      body: (
        <Step5
          certificados={certificadosDisponibles}
          selectedCertificado={selectedCertificado}
          setSelectedCertificado={setSelectedCertificado}
        />
      ),
      onNext: async () => {
        if (!selectedCertificado) {
          show({
            type: "error",
            title: "Sin datos",
            message: "No se ha seleccionado ningún certificado.",
          });
          return false;
        }
        return true;
      },
      showNext: true,
    },
    {
      title: "Calculadora de Utilidades y Alcabalas",
      body: <Step6
        ref={step6Ref}
        onSubmitForm={(data) => setImpuestoCalculado(data)}
        isLoading={isLoading}
        initialValues={datosComprador}
      />,
      showBack: true,
      onBack: async () => {
        if (step6Ref.current) {
          const currentValues = step6Ref.current.getValues?.();
          if (currentValues) {
            setDatosComprador(currentValues);
          }
        }
        return true;
      },
      onNext: async () => {
        setIsLoading(true);
        const result = await step6Ref.current.validateAndSubmit();
        setDatosComprador(result);
        if (!result) {
          setIsLoading(false);
          return false;
        }

        if (result.estadoCuenta !== 'S') {
          show({
            type: "error",
            title: "Cuenta no encontrada",
            message: "El usuario comprador debe crear una cuenta en el sistema",
          });
          setIsLoading(false);
          return false;
        }

        try {
          const resSolvencia = await solvenciaApi.getEsSolvente(result.identificacionComprador);
          if (!resSolvencia.data.data.solvente) {
            show({
              type: "error",
              title: "Contribuyente no solvente",
              message: `El comprador no es solvente. Detalles: ${Object.entries(resSolvencia.data.data.Datos)
                .map(([key, value]) => `${key} - ${value.join(", ")}`)
                .join(", ")}`,
            });
            setIsLoading(false);
            return false;
          }
        } catch (error) {
          show({
            type: "error",
            title: "Error de solvencia",
            message: "No se pudo verificar la solvencia del comprador.",
          });
          setIsLoading(false);
          return false;
        }

        try {
          const responseImpuestoCalculado = await alcabalasApi.calcularImpuesto({
            idvendedor: user.idpersona,
            idcomprador: result.idComprador,
            numerocertificado: selectedCertificado.id,
            valorcompra: result.valorCompra,
            valorventa: result.valorVenta,
            mejorapropia: result.mejoraPropia,
            fechacompra: result.fechaAdquisicion,
            iess: result.iess,
            TipoLiquidacion: result.tipoLiquidacion,
            tipoTraspaso: result.tipoTransaccion
          });
          const datosImpuesto = {
            alcabala: {
              total: responseImpuestoCalculado.data.data[0].Valor,
              detalles: gustavoToJSON(responseImpuestoCalculado.data.data[0].Detalle)
            },
            utilidad: {
              total: responseImpuestoCalculado.data.data[1].Valor,
              detalles: gustavoToJSON(responseImpuestoCalculado.data.data[1].Detalle)
            },
            devaluacion: {
              total: responseImpuestoCalculado.data.data[2].Valor,
              detalles: gustavoToJSON(responseImpuestoCalculado.data.data[2].Detalle)
            },
          };
          setImpuestoCalculado(datosImpuesto);
        } catch (error) {
          console.dir(error.response?.data, { depth: null });
          show({
            type: "error",
            title: error.response?.data?.error || 'Error',
            message: error.response?.data?.msg || error.message || 'Existe itermitencia en la comunicación con el servicio de Portoaguas y/o Dinardap, por favor intente nuevamente'
          });
          return false;
        } finally {
          setIsLoading(false);
        }
        return true;
      },
      showNext: () => !isLoading
    },
    {
      title: "Resultados",
      body: <Step7 impuestoCalculado={impuestoCalculado} />,
      showNext: true,
      onNext: async () => {
        try {
          const responseCertificadoEnTramite = await alcabalasApi.certificadoEnTramite(selectedCertificado.id);
          if (responseCertificadoEnTramite.data.length > 0) {
            setCertificadoEnTramite(true);
          } else {
            setCertificadoEnTramite(false);
          }
        } catch (error) {
          show({
            type: "error",
            title: "Error",
            message: error.message || "Ocurrió un error al validar si existen certificados en trámite del certificado seleccionado. Intente nuévamente.",
          });
          return false;
        }
        return true;
      }
    },
    {
      title: "Resumen",
      body: (
        <Step8
          selectedPredio={selectedPredio}
          datosComprador={datosComprador}
          datosImpuesto={impuestoCalculado}
          certificadoEnTramite={certificadoEnTramite}
        />
      ),
      showNext: true,
    },
    {
      title: "Archivos",
      body: <Step9 ref={step9Ref} setLoading={setIsLoading} show={show} dataForm={step9Files} />,
      onNext: async () => {
        setIsLoading(true);
        try {
          const result = await step9Ref.current.ejecutarSubmit();
          if (!result) return false;

          try {
            const resHistoria = await HistoriaDominioApi.getHistoriaDominio(solvencia);
            let base64Certificado = resHistoria.data?.data?.base64;

            if (!base64Certificado) {
              throw new Error("No se encontró el contenido del certificado de propiedad.");
            }

            // Limpiar la cadena base64 de posibles prefijos y espacios
            base64Certificado = base64Certificado.replace(/^data:.*;base64,/, "").trim();

            const byteCharacters = atob(base64Certificado);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i += 1) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const pdfBlob = new Blob([byteArray], { type: "application/pdf" });
            const certificadoFile = new File(
              [pdfBlob],
              `CERT_PROPIEDAD.pdf`,
              { type: "application/pdf" },
            );

            if (!result.archivos) {
              result.archivos = {};
            }
            result.archivos.certificadoPropiedad = certificadoFile;
          } catch (error) {
            show({
              title: "Error al obtener Certificado de Propiedad",
              message: error.message || "No se pudo obtener automáticamente el Certificado de Propiedad. Intente nuevamente.",
              type: "error",
            });
            return false;
          }

          setStep9Files(result.archivos);

          const [
            escrituraAdquisicion,
            minuta,
            certificadoPropiedad
          ] = await Promise.all([
            crearArchivoObj('ESCR_PROPIEDAD', "ESCRITURA ADQUISICION", result.archivos.escrituraAdquisicion, ".pdf"),
            crearArchivoObj('DOCUMENTO', "MINUTA", result.archivos.minuta, ".pdf"),
            crearArchivoObj('CERT_PROPIEDAD', "CERTIFICADO PROPIEDAD", result.archivos.certificadoPropiedad, ".pdf")
          ]);

          const data = {
            idDeftramite: 160,
            idpersona: user.idpersona,
            Motivo: "Liquidación de Alcabalas y Utilidades",
            solvencia: solvencia,
            clave: selectedPredio.clave,
            ...result,
            escrituraAdquisicion,
            minuta,
            certificadoPropiedad
          }

          onSetDataTramite(data);
          return true;

        } catch (error) {
          show({
            title: "Error de validación",
            message: "Por favor, complete todos los campos requeridos y suba los archivos necesarios.",
            type: "error",
          });
          return false;
        } finally {
          setIsLoading(false);
        }
      },
    },
    {
      title: "Enviar trámite",
      body: <Step10 data={dataTramite} />,
      showBack: false,
      showNext: false,
      onNext: async () => {
        return true;
      },
    },
  ];

  const crearArchivoObj = async (nombre, valor, archivo, extension) => {
    const base64Data = await convertToBase64(archivo);
    return {
      Nombre: nombre.toUpperCase(),
      Valor: valor,
      Detalle: [
        { Nombre: "EXTENSION", Valor: extension },
        { Nombre: "LIBRERIA", Valor: "GENERAL" },
        { Nombre: "DATO", Valor: base64Data.replace(/^data:.*;base64,/, "") },
        { Nombre: "ID", Valor: "0" },
      ],
    };
  };

  const onSetDataTramite = async (data) => {
    const dataSend = {
      idpersona: Number(data.idpersona),
      Motivo: data.Motivo,
      idDeftramite: data.idDeftramite,
      idtarea: 0,
      tipoherencia: 0,
      codigo: "",
      borrador: 0,
      Campos: [
        { Nombre: "ALCABALA", Valor: parseFloat(impuestoCalculado.alcabala.total) },
        { Nombre: "ALCABALAREBAJA", Valor: parseFloat(impuestoCalculado.devaluacion.detalles["Alcabala Rebaja"]) },
        { Nombre: "APROBADOCOMPRADOR", Valor: 'PENDIENTE' },
        { Nombre: "APROBADOVENDEDOR", Valor: 'PENDIENTE' },
        { Nombre: "BASEIMPONIBLE", Valor: parseFloat(impuestoCalculado.devaluacion.detalles["Base Imponible"]) },
        { Nombre: "CERTIFICADOACTUAL", Valor: selectedCertificado.id },
        { Nombre: "CINCOPORCIENTO", Valor: parseFloat(impuestoCalculado.devaluacion.detalles["Descuento Cinco Por Ciento"]) },
        { Nombre: "CLAVECON", Valor: selectedPredio.condominio },
        { Nombre: "CLAVEPRE", Valor: selectedPredio.clave },
        { Nombre: "COMPRADOR", Valor: datosComprador.identificacionComprador },
        { Nombre: "CONSTRUCCIONNUEVA", Valor: 0 },
        { Nombre: "DESCUENTO INVERSION", Valor: parseFloat(impuestoCalculado.utilidad.detalles["Descuento Inversión"]) },
        { Nombre: "DESCUENTOJUNTA", Valor: parseFloat(impuestoCalculado.alcabala.detalles["Junta de Beneficencia"]) },
        { Nombre: "DEVALUACION", Valor: parseFloat(impuestoCalculado.alcabala.total) },
        { Nombre: "DIRECCIONPREDIO", Valor: selectedPredio.Direccion },
        { Nombre: "EMISION", Valor: impuestoCalculado.alcabala.detalles["Emisión"] },
        { Nombre: "EMITIRUTILIDAD", Valor: impuestoCalculado.alcabala.detalles["Emitir Utilidad"] === "SI" },
        { Nombre: "FECHACOMPRA", Valor: selectedCertificado.FAdquisicion ? selectedCertificado.FAdquisicion.split(" ")[0].split("/").reverse().join("/") : "" },
        { Nombre: "FECHALIQUIDACION", Valor: new Date().toLocaleString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).replace(',', '') },
        { Nombre: "GANANCIAEXTRAORDINARIA", Valor: 0.00 },
        { Nombre: "GANANCIAORDINARIA", Valor: 0.00 },
        { Nombre: "IESS", Valor: datosComprador.iess ? 1 : 0 },
        { Nombre: "JUNTAALCABALA", Valor: impuestoCalculado.alcabala.detalles["Junta de Beneficencia"] },
        // { Nombre: "MD5", Valor: result },
        // { Nombre: "MD5CNO", Valor: result },
        // { Nombre: "MD5CSI", Valor: result },
        // { Nombre: "MD5VNO", Valor: result },
        // { Nombre: "MD5VSI", Valor: result },
        { Nombre: "MEJORA", Valor: parseFloat(impuestoCalculado.devaluacion.detalles["Mejora"]) },
        { Nombre: "MEJORALOCAL", Valor: datosComprador.mejoraPropia },
        // { Nombre: "OBSERVACIONLIQUIDACION", Valor: "" },
        { Nombre: "PARCIAL", Valor: "NO" },
        { Nombre: "PLUSVALIA", Valor: parseFloat(impuestoCalculado.utilidad.detalles["Plusvalía"]) },
        { Nombre: "PORCENTAJEALCABALAREBAJA", Valor: parseFloat(impuestoCalculado.devaluacion.detalles["Alcabala Rebaja"]) },
        { Nombre: "PORCENTAJEDESCUENTO", Valor: parseFloat(impuestoCalculado.devaluacion.detalles["Porcentaje Descuento"]) },
        { Nombre: "PORCENTAJEDEVALUACION", Valor: parseFloat(impuestoCalculado.devaluacion.detalles["Porcentaje Devaluacion"]) },
        { Nombre: "PORCENTAJETERCERAEDADALCABALA", Valor: parseFloat(impuestoCalculado.devaluacion.detalles["Porcentaje Tercera Edad Alcabala"]) },
        { Nombre: "PORCENTAJETERCERAEDADUTILIDAD", Valor: parseFloat(impuestoCalculado.devaluacion.detalles["Porcentaje Tercera Edad Utilidad"]) },
        { Nombre: "PRECIOAJUSTADO", Valor: 0.00 },
        { Nombre: "RELIQUIDADO", Valor: 0 },
        { Nombre: "TERCERAEDADALCABALA", Valor: parseFloat(impuestoCalculado.alcabala.detalles["Descuento Tercera Edad Alcabala"]) },
        { Nombre: "TERCERAEDADUTILIDAD", Valor: parseFloat(impuestoCalculado.utilidad.detalles["Descuento Tercera Edad"]) },
        { Nombre: "TIPOLIQUIDACIÓN", Valor: datosComprador.tipoLiquidacion },
        { Nombre: "TIPOTRANSACCION", Valor: datosComprador.tipoTransaccion },
        { Nombre: "TOTALALCABALA", Valor: parseFloat(impuestoCalculado.alcabala.detalles.Emisión) },
        { Nombre: "TOTALUTILIDAD", Valor: parseFloat(impuestoCalculado.utilidad.detalles.Emisión) },
        { Nombre: "ULTIMOAVALUO", Valor: parseFloat(impuestoCalculado.devaluacion.detalles["Ultimo Avaluo"]) },
        { Nombre: "UTILIDAD", Valor: parseFloat(impuestoCalculado.utilidad.total) },
        // { Nombre: "VALIDADOR", Valor: result },
        // { Nombre: "VALIDADORVENDEDOR", Valor: result },
        { Nombre: "VALORCOMPRA", Valor: parseFloat(datosComprador.valorCompra) },
        { Nombre: "VALORESPECULATIVO", Valor: 0.00 },
        { Nombre: "VALORVENTA", Valor: parseFloat(datosComprador.valorVenta) },
        { Nombre: "VENDEDOR", Valor: user.cedularuc },
        { Nombre: "VENTACOMPRADORPUBLICO", Valor: parseFloat(impuestoCalculado.devaluacion.detalles["Comprador Publico"]) }
      ],
      Archivos: [data.escrituraAdquisicion, data.minuta, data.certificadoPropiedad],
    };
    setDataTramite(dataSend);
  };

  return (
    <>
      <Breadcrumb pageName="Liquidación de Alcabalas" />
      <div className="grid grid-cols-1 gap-4">
        <Stepper2
          steps={steps}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
      </div>
    </>
  );
};

export default LiquidacionAlcabales;